import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '@environments/environment';
import { filter, Observable, tap } from 'rxjs';
import {
  Channel,
  SocketService,
} from 'src/app/shared/components/in-progress-processes/socket.service';
import {
  IInsuranceIntelligence,
  ISmartUploadProcess,
  SmartUploadProcessStatus,
  TxStatus,
} from './submission-link-upload.models';
import { DirectUpload } from '@rails/activestorage';

@Injectable({
  providedIn: 'root',
})
export class SubmissionLinkUploadService {
  smartUploadProcesses: WritableSignal<ISmartUploadProcess[]> = signal([]);
  private _http = inject(HttpClient);
  private _socketService = inject(SocketService);

  uploadDocuments(files: File[]): Observable<{ upload_id: string }> {
    const url = `${environment.apiBaseUri}/universal_submit_upload`;
    const formData = new FormData();

    files.forEach((file) => formData.append('files[]', file, file.name));

    return this._http.post<{ upload_id: string }>(url, formData).pipe(
      filter((response) => !!response.upload_id),
      tap(({ upload_id }) => {
        this.smartUploadProcesses.update((processes) => [
          ...processes,
          {
            upload_id,
            filenames: files.map((file) => file.name),
            application_id: null,
            status: SmartUploadProcessStatus.Extracting,
          },
        ]);

        this.startWatchingUpload(upload_id);
      }),
    );
  }

  directUpload(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const url = `${environment.apiBaseUri}/rails/active_storage/direct_uploads`;
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.withCredentials = true;
        },
      });

      upload.create((error, blob) => {
        if (error) {
          reject(error);
        } else {
          resolve(blob?.signed_id ?? '');
        }
      });
    });
  }

  saveSignedIds(signedIds: string[]): Observable<any> {
    const url = `${environment.apiBaseUri}/universal_submit_upload/create_from_client_uploads`;

    return this._http
      .post(url, {
        documents: signedIds,
      })
      .pipe(
        filter((response: { upload_id: string }) => !!response.upload_id),
        tap(({ upload_id, files }) => {
          this.smartUploadProcesses.update((processes) => [
            ...processes,
            {
              upload_id,
              filenames: files.map((file) => file.filename),
              application_id: null,
              status: SmartUploadProcessStatus.Extracting,
            },
          ]);

          this.startWatchingUpload(upload_id);
        }),
      );
  }

  getDocument(documentUrl: string) {
    const url = `${environment.apiBaseUri}${documentUrl}`;
    return this._http.get(url, { responseType: 'blob' });
  }

  getDocuments(documentUrls: string[]): Observable<Blob>[] {
    return documentUrls.map((url) => this.getDocument(url));
  }

  private startWatchingUpload(uploadId: string): void {
    this._socketService.subscribe(Channel.UniversalSubmit, uploadId, (data) =>
      this.receivedUniversalSubmitUpdate(data, uploadId),
    );
  }

  private receivedUniversalSubmitUpdate(
    data: IInsuranceIntelligence,
    uploadId: string,
  ): void {
    const status = this.mapTxStatusToStatus(data);
    this.smartUploadProcesses.update((processes) =>
      processes.map((process) =>
        process.upload_id === uploadId
          ? {
              ...process,
              application_id: data.quote_application?.application_id ?? null,
              status,
            }
          : process,
      ),
    );

    if (
      status === SmartUploadProcessStatus.Completed ||
      status === SmartUploadProcessStatus.Failed
    ) {
      this._socketService.unsubscribeFromChannel(
        Channel.UniversalSubmit,
        uploadId,
      );
    }
  }

  private mapTxStatusToStatus(
    insuranceIntelligence: IInsuranceIntelligence,
  ): SmartUploadProcessStatus {
    if (
      insuranceIntelligence.quote_application?.application_id &&
      (insuranceIntelligence.quote_application.tx_status ===
        TxStatus.Completed ||
        insuranceIntelligence.quote_application.tx_status === TxStatus.Failed ||
        insuranceIntelligence.quote_application.tx_status === TxStatus.TimedOut)
    ) {
      return SmartUploadProcessStatus.Completed;
    } else if (
      insuranceIntelligence.quote_application?.tx_status === TxStatus.Failed ||
      insuranceIntelligence.document_processing.tx_status === TxStatus.Failed
    ) {
      return SmartUploadProcessStatus.Failed;
    } else if (insuranceIntelligence.quote_application?.application_id) {
      return SmartUploadProcessStatus.Processing;
    }
    return SmartUploadProcessStatus.Extracting;
  }
}
